import * as React from "react";
import PasswordInput from "../../../Common/Forms/PasswordInput";
import RetractableInput from "../../../Common/Forms/RetractableInput";
import { getOption } from "../../../Utilities/Platform/Options/Options";
import LoginUsername from "./LoginUsername";

type Props = {
    username: string;
    password: string;
    showUsername: boolean;
    touchNumbers: boolean;
    requirePassword: boolean;
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onLogin: () => void;
    disabled?: boolean;
};

export default class LoginPassword extends React.Component<Props> {
    public maskUsername = getOption("sign_in_mask_username");

    public usernameRef = React.createRef<HTMLInputElement>();
    public passwordRef = React.createRef<HTMLInputElement>();

    protected receiptEl = document.querySelector("#login-box .receipt-template-select");

    public componentDidMount(): void {
        this.focus();
        document.addEventListener("click", this.handleClickFocus);
    }

    public componentWillUnmount(): void {
        document.removeEventListener("click", this.handleClickFocus);
    }

    public handleClickFocus = (e: MouseEvent): void => {
        const target = e.target as Element;

        if(target?.tagName === "INPUT") {
            return;
        }

        if(this.receiptEl === null) {
            const receiptEl = document.querySelector("#login-box .receipt-template-select");

            if(receiptEl !== null) {
                this.receiptEl = receiptEl;
            }
        }

        if(this.receiptEl !== null && this.receiptEl.contains(target)) {
            return;
        }

        const menuWrapper = document.querySelector(".receipt-template-select-menu");

        if(menuWrapper !== null) {
            if(menuWrapper.contains(target)) {
                return;
            }
        }

        this.focus();
    };

    public handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
        if(e.key === "Enter") {
            const name = e.currentTarget.name;

            if(name === "shopfront-field-u") {
                if(this.props.requirePassword) {
                    this.passwordRef.current?.focus({
                        preventScroll: true,
                    });
                } else {
                    this.props.onLogin();
                }
            } else if(name === "shopfront-field-p") {
                this.props.onLogin();
            }
        }
    };

    public focus = (select = false): void => {
        if(this.props.showUsername && this.usernameRef.current && !this.props.username) {
            if(this.usernameRef.current) {
                this.usernameRef.current.focus({
                    preventScroll: true,
                });

                if(select) {
                    this.usernameRef.current.select();
                }
            }
        } else if(this.props.requirePassword) {
            if(this.passwordRef.current) {
                this.passwordRef.current.focus({
                    preventScroll: true,
                });

                if(select) {
                    this.passwordRef.current.select();
                }
            }
        }
    };

    public stopPropagation = (e: React.SyntheticEvent): void => {
        e.stopPropagation();
    };

    public render = (): React.ReactNode => {
        let username, password;

        if(this.props.showUsername) {
            username = (
                <RetractableInput
                    label="Username"
                    hasValue={!!this.props.username}
                >
                    {(onFocus, onBlur): React.ReactNode => (
                        <LoginUsername
                            maskUsername={this.maskUsername}
                            ref={this.usernameRef}
                            id="shopfront-field-u"
                            name="shopfront-field-u"
                            autoComplete="shopfront-field-u"
                            className="p-4"
                            value={this.props.username}
                            onChange={this.props.onChange}
                            onKeyDown={this.handleKeyDown}
                            onClick={this.stopPropagation}
                            data-testid="sl-username"
                            onFocus={onFocus}
                            onBlur={onBlur}
                            disabled={this.props.disabled}
                        />
                    )}
                </RetractableInput>
            );
        }

        if(this.props.requirePassword) {
            password = (
                <RetractableInput
                    label="Password"
                    hasValue={!!this.props.password}
                >
                    {(onFocus, onBlur): React.ReactNode => (
                        <PasswordInput
                            onFocus={onFocus}
                            onBlur={onBlur}
                            autoComplete="shopfront-field-p"
                            name="shopfront-field-p"
                            id="shopfront-field-p"
                            className="p-4"
                            value={this.props.password}
                            onChange={this.props.onChange}
                            ref={this.passwordRef}
                            onKeyDown={this.handleKeyDown}
                            data-testid="sl-password"
                            onClick={this.stopPropagation}
                            disabled={this.props.disabled}
                        />
                    )}
                </RetractableInput>
            );
        }

        return (
            <div id="login-password">
                <form
                    onSubmit={this.stopPropagation}
                    method="post"
                    autoComplete="shopfront-off" /* To prevent Chrome from complaining */
                >
                    {/* Pretend username and password fields to absorb the autocomplete */}
                    <input
                        type="text"
                        className="autocomplete-absorber"
                        name="username"
                        autoComplete="username"
                        disabled={this.props.disabled}
                    />
                    <input
                        type="password"
                        className="autocomplete-absorber"
                        name="password"
                        autoComplete="password"
                        disabled={this.props.disabled}
                    />
                    {/* The real username and password fields */}
                    {username}
                    {password}
                </form>
            </div>
        );
    };
}
